import { logger } from '../../utils/core/logger';
import { CTAPageFormStrategy } from './strategies/CTAPageFormStrategy';
import { type DataLayerConversionEvent, DataLayerStrategy } from './strategies/DataLayerStrategy';
import { DomObserverStrategy } from './strategies/DomObserverStrategy';
import { HubSpotFormStrategy } from './strategies/HubSpotFormStrategy';
import { NetworkObserverStrategy } from './strategies/NetworkObserverStrategy';
import { PostMessageStrategy } from './strategies/PostMessageStrategy';
import { SuperformStrategy } from './strategies/SuperformStrategy';
import { ThankYouPageStrategy } from './strategies/ThankYouPageStrategy';
import { UnbounceFormStrategy } from './strategies/UnbounceFormStrategy';
import type {
    FormDetectionContext,
    FormDetectionManagerInterface,
    FormDetectionStrategy,
    FormElement,
} from './types';

interface FormStrategyToggles {
    superform: boolean;
    hubspot: boolean;
    cta_page_form: boolean;
    thankyou_page: boolean;
    network_observer: boolean;
    dom_observer: boolean;
    dom_observer_track_wrapped_forms: boolean;
    post_message: boolean;
    data_layer: boolean;
}

interface FormDetectionManagerOptions {
    dataLayerConversionEvents?: DataLayerConversionEvent[];
}

// Main Form Detection Manager
export class FormDetectionManager implements FormDetectionManagerInterface {
    private strategies: FormDetectionStrategy[] = [];
    private trackedForms = new WeakSet<FormElement>();
    private submissionTracking = new Map<FormElement, number>(); // form -> last submission timestamp
    private readonly DEDUPLICATION_WINDOW = 2000; // 2 seconds
    private mutationObserver: MutationObserver | null = null;
    private isObserving = false;
    private turboLoadListener: (() => void) | null = null;
    private turboRenderListener: (() => void) | null = null;
    private thankYouPageStrategy: ThankYouPageStrategy | null = null;
    private networkObserverStrategy: NetworkObserverStrategy | null = null;
    private domObserverStrategy: DomObserverStrategy | null = null;
    private postMessageStrategy: PostMessageStrategy | null = null;
    private dataLayerStrategy: DataLayerStrategy | null = null;
    private strategyToggles: FormStrategyToggles;
    private dataLayerConversionEvents: DataLayerConversionEvent[];

    constructor(
        strategies?: FormDetectionStrategy[],
        strategyToggles?: FormStrategyToggles,
        options?: FormDetectionManagerOptions
    ) {
        this.strategyToggles = strategyToggles ?? {
            superform: true,
            hubspot: true,
            cta_page_form: true,
            thankyou_page: true,
            network_observer: true,
            dom_observer: false,
            dom_observer_track_wrapped_forms: false,
            post_message: true,
            data_layer: true,
        };
        this.dataLayerConversionEvents = options?.dataLayerConversionEvents ?? [];

        if (strategies) {
            this.strategies = strategies;
        } else {
            // Build default strategies based on toggles
            // NOTE: Strategies are applied in priority order - first matching strategy wins
            const defaults: FormDetectionStrategy[] = [];
            if (this.strategyToggles.superform) {
                defaults.push(new SuperformStrategy('sf-step', 'step9')); // Priority 1: Webflow Superform multi-step forms
            }
            if (this.strategyToggles.hubspot) {
                defaults.push(new HubSpotFormStrategy()); // Priority 2: HubSpot React forms
            }
            // Priority 3: Unbounce forms - must run before the generic CTA
            // strategy so Unbounce LPs use Unbounce's own afterFormSubmit hook
            // (a real success signal) instead of the submit-event heuristic.
            // Gated via canHandle to /fsg forms with the hook API available, so
            // everything else falls through to CTAPageFormStrategy.
            defaults.push(new UnbounceFormStrategy());
            if (this.strategyToggles.cta_page_form) {
                defaults.push(new CTAPageFormStrategy()); // Priority 4: Standard form detection
            }
            this.strategies = defaults;

            logger.debug('📦 Form detection strategies initialized', {
                enabled: Object.entries(this.strategyToggles)
                    .filter(([, v]) => v)
                    .map(([k]) => k),
                disabled: Object.entries(this.strategyToggles)
                    .filter(([, v]) => !v)
                    .map(([k]) => k),
            });
        }

        // Set manager reference on strategies that need it
        this.strategies.forEach((strategy) => {
            if ('setManager' in strategy && typeof strategy.setManager === 'function') {
                strategy.setManager(this);
            }
        });
    }

    // Add a new detection strategy
    addStrategy(strategy: FormDetectionStrategy): void {
        // Set manager reference if strategy supports it
        if ('setManager' in strategy && typeof strategy.setManager === 'function') {
            strategy.setManager(this);
        }
        this.strategies.push(strategy);
        logger.debug(`📦 Added form detection strategy: ${strategy.name}`);
    }

    // Remove a strategy by name
    removeStrategy(name: string): void {
        this.strategies = this.strategies.filter((s) => s.name !== name);
        logger.debug(`🗑️ Removed form detection strategy: ${name}`);
    }

    // Check if submission should be tracked (deduplication)
    // Note: Called from strategy instances via this.manager reference
    public shouldTrackSubmission(form: FormElement): boolean {
        const lastSubmission = this.submissionTracking.get(form);
        const now = Date.now();

        if (lastSubmission && now - lastSubmission < this.DEDUPLICATION_WINDOW) {
            logger.debug('⏭️ Skipping duplicate form submission within deduplication window', {
                formId: form.id,
                timeSinceLastSubmission: now - lastSubmission,
            });
            return false; // Duplicate submission within window
        }

        this.submissionTracking.set(form, now);

        // Clean up old entries after window expires
        setTimeout(() => {
            this.submissionTracking.delete(form);
        }, this.DEDUPLICATION_WINDOW);

        return true;
    }

    // Detect form submission result using heuristics
    // Note: Called from strategy instances via this.manager reference
    public detectFormResult(form: FormElement): {
        status: 'success' | 'failed' | 'unknown';
        indicators: string[];
    } {
        const indicators: string[] = [];

        // Diagnostic (form-tracking diagnosis): dump candidate success/error nodes to help
        // tune keyword/selector matching for inline forms (esp. non-English). Guarded behind
        // the debug log level so the querySelectorAll/getComputedStyle work is skipped in prod
        // (loglevel: TRACE=0, DEBUG=1; default prod level is WARN=3).
        if (logger.getLevel() <= 1) {
            try {
                const dump = (els: Element[]) =>
                    els
                        .filter((el) => window.getComputedStyle(el).display !== 'none')
                        .map((el) => ({
                            tag: el.tagName.toLowerCase(),
                            cls: el.className || '',
                            role: el.getAttribute('role') || '',
                            ariaLive: el.getAttribute('aria-live') || '',
                            text: (el.textContent || '').trim().slice(0, 160),
                        }));
                logger.debug('🔬 detectFormResult DOM candidates', {
                    formId: form.id,
                    url: window.location.href,
                    formInDom: document.body.contains(form),
                    successLike: dump(
                        Array.from(
                            document.querySelectorAll(
                                '.success,.confirmation,.thank-you,.success-message,[role="status"],[aria-live="polite"],[aria-live="assertive"]'
                            )
                        )
                    ),
                    errorLike: dump(
                        Array.from(
                            document.querySelectorAll(
                                '.error,.alert,.error-message,.validation-error,[role="alert"],.has-error,.invalid-feedback'
                            )
                        )
                    ),
                    formText: (form.textContent || '').trim().slice(0, 300),
                });
            } catch {
                // never let debug logging break detection
            }
        }

        // Check if form still exists in DOM
        if (!document.body.contains(form)) {
            indicators.push('form_removed_from_dom');
            return { status: 'success', indicators };
        }

        // Check if form is hidden
        const computedStyle = window.getComputedStyle(form);
        if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
            indicators.push('form_hidden');
            return { status: 'success', indicators };
        }

        // Check for success messages
        const successSelectors = [
            '.success',
            '.confirmation',
            '.thank-you',
            '.success-message',
            '[role="status"]',
            '[aria-live="polite"]',
        ];

        for (const selector of successSelectors) {
            const successElement = document.querySelector(selector);
            if (
                successElement?.textContent &&
                (successElement.textContent.toLowerCase().includes('success') ||
                    successElement.textContent.toLowerCase().includes('thank you') ||
                    successElement.textContent.toLowerCase().includes('submitted'))
            ) {
                indicators.push(`success_message_found: ${selector}`);
                return { status: 'success', indicators };
            }
        }

        // Check for error messages
        const errorSelectors = [
            '.error',
            '.alert',
            '.error-message',
            '.validation-error',
            '[role="alert"]',
            '.has-error',
            '.invalid-feedback',
        ];

        for (const selector of errorSelectors) {
            const errorElement = form.querySelector(selector) || document.querySelector(selector);
            if (errorElement && window.getComputedStyle(errorElement).display !== 'none') {
                indicators.push(`error_message_found: ${selector}`);
                return { status: 'failed', indicators };
            }
        }

        // Check if form was reset (all input fields are empty)
        const inputs = form.querySelectorAll('input, textarea, select');
        let allEmpty = true;
        let totalInputs = 0;

        inputs.forEach((input) => {
            if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
                if (input.type !== 'hidden' && input.type !== 'submit' && input.type !== 'button') {
                    totalInputs++;
                    if (input.value.trim() !== '') {
                        allEmpty = false;
                    }
                }
            } else if (input instanceof HTMLSelectElement) {
                totalInputs++;
                if (input.selectedIndex > 0) {
                    allEmpty = false;
                }
            }
        });

        if (totalInputs > 0 && allEmpty) {
            indicators.push('form_reset_detected');
            return { status: 'success', indicators };
        }

        // Check for validation errors (HTML5 validation)
        const invalidInputs = form.querySelectorAll(
            'input:invalid, textarea:invalid, select:invalid'
        );
        if (invalidInputs.length > 0) {
            indicators.push(`validation_errors: ${invalidInputs.length}`);
            return { status: 'failed', indicators };
        }

        // Unable to determine - return unknown
        indicators.push('no_clear_indicators');
        return { status: 'unknown', indicators };
    }

    // Apply first matching strategy to a form
    trackForm(form: FormElement, context: FormDetectionContext): void {
        // Skip if already tracked
        if (this.trackedForms.has(form) || form.__ctaFormTracked) {
            return;
        }

        logger.debug('🔍 Checking form element for tracking', {
            formId: form.id,
            formTagName: form.tagName,
            formClass: form.className,
            hasSfAttribute: form.hasAttribute('sf'),
            hasStepChild: form.querySelector('[sf-step]') !== null,
        });

        // Find first applicable strategy (strategies are ordered by priority)
        const applicableStrategy = this.strategies.find((strategy) =>
            strategy.canHandle(form, context)
        );

        if (!applicableStrategy) {
            logger.debug('⚠️ No applicable strategy found for form', {
                formId: form.id,
                formTagName: form.tagName,
                formClass: form.className,
            });
            return;
        }

        logger.debug(`📝 Applying strategy '${applicableStrategy.name}' to form`, {
            formId: form.id,
            formTagName: form.tagName,
            formClass: form.className,
        });

        // Apply the strategy
        try {
            applicableStrategy.apply(form, context);
        } catch (error) {
            logger.warn(`Failed to apply strategy ${applicableStrategy.name}:`, error);
        }

        // Mark as tracked
        this.trackedForms.add(form);
        form.__ctaFormTracked = true;
    }

    // Track all forms on the page
    trackAllForms(context: FormDetectionContext): number {
        const forms = document.querySelectorAll('form');
        let newFormsTracked = 0;

        logger.debug('📋 trackAllForms: Found forms on page', {
            totalForms: forms.length,
            formIds: Array.from(forms).map((f) => f.id || 'no-id'),
        });

        // Track regular forms
        forms.forEach((form) => {
            const formElement = form as FormElement;

            // Skip if already tracked
            if (this.trackedForms.has(formElement) || formElement.__ctaFormTracked) {
                logger.debug('⏭️ Skipping already tracked form', { formId: formElement.id });
                return;
            }

            // Skip hidden forms or forms with very generic/placeholder IDs
            const isHidden =
                formElement.style.display === 'none' ||
                formElement.style.visibility === 'hidden' ||
                formElement.offsetParent === null;

            if (isHidden) {
                logger.debug('⏭️ Skipping hidden form', {
                    formId: formElement.id,
                    displayStyle: formElement.style.display,
                    visibilityStyle: formElement.style.visibility,
                    offsetParent: formElement.offsetParent,
                });
                return;
            }

            logger.debug('✅ Attempting to track form', { formId: formElement.id });
            this.trackForm(formElement, context);
            if (this.trackedForms.has(formElement)) {
                newFormsTracked++;
            }
        });

        // If no forms were actually tracked (either none exist or all were hidden/skipped),
        // try formless page strategies for pages with orphan inputs.
        if (newFormsTracked === 0) {
            if (!this.networkObserverStrategy && this.strategyToggles.network_observer) {
                this.networkObserverStrategy = new NetworkObserverStrategy();
                const activated = this.networkObserverStrategy.checkAndActivate(context);
                if (activated) {
                    logger.debug('📄 NetworkObserverStrategy activated (no <form> elements found)');
                } else {
                    this.networkObserverStrategy = null;
                }
            }
        }

        this.maybeActivateDomObserver(context, newFormsTracked);

        return newFormsTracked;
    }

    /**
     * Activate DomObserver when no <form> was tracked (formless SPA page) OR, when the
     * dom_observer_track_wrapped_forms opt-in is set, even if a <form> was tracked — this
     * covers SPAs that wrap their inputs in a <form> whose submit button lives outside it
     * (e.g. PayFit welcome funnel), where CTAPageForm claims the form but can never fire.
     *
     * Called from the initial scan AND from the dynamic-form MutationObserver: in a React
     * SPA the conversion form is added on client-side navigation (no Turbo events), so the
     * initial trackAllForms scan never sees it. DomObserver.checkAndActivate returns false
     * when no qualifying inputs/button exist and is idempotent once active, so re-calling
     * is a safe no-op for other pages.
     */
    private maybeActivateDomObserver(context: FormDetectionContext, formsTracked: number): void {
        if (this.domObserverStrategy) return;
        if (!this.strategyToggles.dom_observer) {
            if (this.strategyToggles.dom_observer_track_wrapped_forms) {
                logger.debug(
                    'dom_observer_track_wrapped_forms ignored because dom_observer is disabled'
                );
            }
            return;
        }

        const tryDomObserver =
            formsTracked === 0 || this.strategyToggles.dom_observer_track_wrapped_forms;
        if (!tryDomObserver) return;

        this.domObserverStrategy = new DomObserverStrategy(
            this.strategyToggles.dom_observer_track_wrapped_forms
        );
        const activated = this.domObserverStrategy.checkAndActivate(context);
        if (activated) {
            logger.debug('📄 DomObserverStrategy activated', {
                trackWrappedForms: this.strategyToggles.dom_observer_track_wrapped_forms,
                formsTracked,
            });
        } else {
            this.domObserverStrategy = null;
        }
    }

    // Start observing DOM for dynamically added forms
    startObserving(context: FormDetectionContext, options?: { initialDelay?: number }): void {
        if (this.isObserving) {
            logger.debug('🔍 Form observer already running');
            return;
        }

        const initialDelay = options?.initialDelay ?? 0;

        // Initial scan after optional delay
        const performInitialScan = () => {
            const initialCount = this.trackAllForms(context);
            logger.debug(`📋 Initial form scan found ${initialCount} new forms`);

            // Initialize thank-you page detection (URL-based form completion detection)
            if (this.strategyToggles.thankyou_page) {
                this.thankYouPageStrategy = new ThankYouPageStrategy();
                this.thankYouPageStrategy.checkCurrentUrl(context); // Check on initial load
                this.thankYouPageStrategy.setupUrlMonitoring(context); // Setup SPA navigation monitoring
            }

            // Initialize postMessage detection (iframe-based form providers: HubSpot, Cal.com, Typeform)
            if (this.strategyToggles.post_message) {
                this.postMessageStrategy = new PostMessageStrategy();
                this.postMessageStrategy.checkAndActivate(context);
            }

            // Initialize dataLayer detection (GTM events: hubspot-form-success, custom client events)
            if (this.strategyToggles.data_layer) {
                this.dataLayerStrategy = new DataLayerStrategy(this.dataLayerConversionEvents);
                this.dataLayerStrategy.checkAndActivate(context);
            }

            // Start observing for dynamically added forms
            this.mutationObserver = new MutationObserver((mutations) => {
                let formsAdded = false;

                for (const mutation of mutations) {
                    // Check added nodes for forms
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element;

                            // Check if the added node is a form
                            if (element.tagName === 'FORM') {
                                const formElement = element as FormElement;
                                if (
                                    !this.trackedForms.has(formElement) &&
                                    !formElement.__ctaFormTracked
                                ) {
                                    // Skip hidden forms
                                    const isHidden =
                                        formElement.style.display === 'none' ||
                                        formElement.style.visibility === 'hidden' ||
                                        formElement.offsetParent === null;

                                    if (isHidden) {
                                        logger.debug('⏭️ Skipping dynamically added hidden form', {
                                            formId: formElement.id,
                                        });
                                        return;
                                    }

                                    logger.debug('📝 Detected dynamically added form', {
                                        formId: formElement.id,
                                        formClass: formElement.className,
                                    });
                                    this.trackForm(formElement, context);
                                    formsAdded = true;
                                }
                            }

                            // Check if the added node contains forms
                            const nestedForms = element.querySelectorAll('form');
                            if (nestedForms.length > 0) {
                                nestedForms.forEach((form) => {
                                    const formElement = form as FormElement;
                                    if (
                                        !this.trackedForms.has(formElement) &&
                                        !formElement.__ctaFormTracked
                                    ) {
                                        // Skip hidden forms
                                        const isHidden =
                                            formElement.style.display === 'none' ||
                                            formElement.style.visibility === 'hidden' ||
                                            formElement.offsetParent === null;

                                        if (isHidden) {
                                            logger.debug(
                                                '⏭️ Skipping dynamically added hidden nested form',
                                                { formId: formElement.id }
                                            );
                                            return;
                                        }

                                        logger.debug('📝 Detected dynamically added nested form', {
                                            formId: formElement.id,
                                            formClass: formElement.className,
                                        });
                                        this.trackForm(formElement, context);
                                        formsAdded = true;
                                    }
                                });
                            }
                        }
                    });
                }

                if (formsAdded) {
                    logger.debug('✨ Finished tracking dynamically added forms');
                }

                // Wrapped-form DomObserver activation (opt-in, e.g. PayFit welcome funnel).
                // React SPAs add the conversion <form> on client-side navigation with no
                // Turbo event, so the initial scan never sees it — and the <form> node can
                // be inserted BEFORE its inputs render, so a form-add alone isn't enough.
                // Retry on every mutation batch until activation succeeds; guarded so it is
                // a no-op once active and for clients without the opt-in.
                if (
                    !this.domObserverStrategy &&
                    this.strategyToggles.dom_observer &&
                    this.strategyToggles.dom_observer_track_wrapped_forms
                ) {
                    this.maybeActivateDomObserver(context, 1);
                }
            });

            // Start observing
            this.mutationObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });

            this.isObserving = true;
            logger.debug('🔍 Started observing DOM for new forms');

            // Add Turbo navigation event listeners for re-scanning forms
            this.turboLoadListener = () => {
                logger.debug('🔄 Turbo navigation detected (turbo:load), rescanning for forms');
                this.trackAllForms(context);
            };
            this.turboRenderListener = () => {
                logger.debug('🔄 Turbo render detected (turbo:render), rescanning for forms');
                this.trackAllForms(context);
            };

            document.addEventListener('turbo:load', this.turboLoadListener);
            document.addEventListener('turbo:render', this.turboRenderListener);
            logger.debug('🔍 Added Turbo event listeners for form detection');
        };

        if (initialDelay > 0) {
            logger.debug(`⏱️ Waiting ${initialDelay}ms before initial form scan`);
            setTimeout(performInitialScan, initialDelay);
        } else {
            performInitialScan();
        }
    }

    // Stop observing for new forms
    stopObserving(): void {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
            this.isObserving = false;
            logger.debug('🛑 Stopped observing DOM for new forms');
        }

        // Remove Turbo event listeners
        if (this.turboLoadListener) {
            document.removeEventListener('turbo:load', this.turboLoadListener);
            this.turboLoadListener = null;
            logger.debug('🛑 Removed turbo:load event listener');
        }
        if (this.turboRenderListener) {
            document.removeEventListener('turbo:render', this.turboRenderListener);
            this.turboRenderListener = null;
            logger.debug('🛑 Removed turbo:render event listener');
        }

        // Cleanup thank-you page strategy
        if (this.thankYouPageStrategy) {
            this.thankYouPageStrategy.cleanup();
            this.thankYouPageStrategy = null;
        }

        // Cleanup network observer strategy
        if (this.networkObserverStrategy) {
            this.networkObserverStrategy.cleanup();
            this.networkObserverStrategy = null;
        }

        // Cleanup DOM observer strategy
        if (this.domObserverStrategy) {
            this.domObserverStrategy.cleanup();
            this.domObserverStrategy = null;
        }

        // Cleanup postMessage strategy
        if (this.postMessageStrategy) {
            this.postMessageStrategy.cleanup();
            this.postMessageStrategy = null;
        }

        // Cleanup dataLayer strategy
        if (this.dataLayerStrategy) {
            this.dataLayerStrategy.cleanup();
            this.dataLayerStrategy = null;
        }
    }

    // Clean up all strategies
    cleanup(): void {
        // Stop observing for new forms
        this.stopObserving();

        // Cleanup strategies
        this.strategies.forEach((strategy) => {
            if (strategy.cleanup) {
                strategy.cleanup();
            }
        });

        logger.debug('🧹 Form detection cleanup completed');
    }

    // Debug helper - test if a form would be detected as Superform
    debugCanHandle(formSelector: string = 'form'): void {
        const form = document.querySelector(formSelector) as FormElement;
        if (!form) {
            logger.debug('❌ No form found with selector:', formSelector);
            return;
        }

        logger.debug('🔍 Testing form detection:', {
            formId: form.id,
            formClass: form.className,
        });

        // Create a dummy context for debugging
        const debugContext: FormDetectionContext = {
            domain: window.location.hostname,
            currentUrl: window.location.href,
            onFormSubmit: () => {},
        };

        this.strategies.forEach((strategy) => {
            const canHandle = strategy.canHandle(form, debugContext);
            logger.debug(`${canHandle ? '✅' : '❌'} ${strategy.name}:`, canHandle);
        });
    }
}

// Export a singleton instance for convenience
const defaultFormDetectionManager = new FormDetectionManager();

// Expose debug helper globally for console access
if (typeof window !== 'undefined') {
    // biome-ignore lint/suspicious/noExplicitAny: debug helper exposed on window for console access
    (window as any).__debugFormDetection = (selector?: string) => {
        defaultFormDetectionManager.debugCanHandle(selector);
    };
}
